import { createFileRoute } from '@tanstack/react-router'
import { LayoutGroup } from 'motion/react'
import { useEffect, useState } from 'react'

import { useMe } from '@/features/auth/api'
import { useLounge } from '@/features/lounges/api'
import { Composer } from '@/features/messages/Composer'
import { MessageTimeline } from '@/features/messages/MessageTimeline'
import { TypingIndicator } from '@/features/messages/TypingIndicator'
import {
  type TimelineMessage,
  messagesInfiniteQueryOptions,
  useMessageStream,
} from '@/features/messages/api'
import { useNook } from '@/features/nooks/api'
import { VoiceJoinButton } from '@/features/voice/VoiceCluster'
import { VoiceStage } from '@/features/voice/VoiceStage'
import { useVoice } from '@/features/voice/useVoice'

import '@/features/messages/messages.css'

/**
 * One nook: its header, who is in it, its history, and the box you type in.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug/n/$nookId')({
  loader: ({ context: { queryClient }, params: { nookId } }) =>
    // The same options object the component uses, so navigating here preloads
    // the first page and the view never renders empty.
    queryClient.ensureInfiniteQueryData(messagesInfiniteQueryOptions(nookId)),
  component: NookView,
})

function NookView() {
  const { loungeSlug, nookId } = Route.useParams()
  const { data: meData } = useMe()
  const { data: loungeData } = useLounge(loungeSlug)
  const { data: nookData } = useNook(nookId)
  const voice = useVoice()

  const [replyingTo, setReplyingTo] = useState<TimelineMessage | null>(null)
  const [expanded, setExpanded] = useState(false)

  // A reply belongs to the room it was started in.
  useEffect(() => setReplyingTo(null), [nookId])
  // So does an expanded stage: arriving in a new room with the timeline already
  // hidden would be the app deciding for you what you came to do. Leaving voice
  // closes it for the same reason from the other side — a full-height stage
  // holding a strip of faces and no conversation is not a state anybody asked
  // for.
  useEffect(() => {
    if (voice.nookId !== nookId) setExpanded(false)
  }, [voice.nookId, nookId])

  useMessageStream(nookId, meData?.me?.id)

  const me = meData?.me
  const lounge = loungeData?.lounge
  const nook = nookData?.nook

  if (!me || !lounge || !nook) return null

  const canModerate = lounge.myRole === 'OWNER' || lounge.myRole === 'MOD'

  return (
    // One group spanning header and stage, because that span *is* the signature
    // transition: the Join button and the control cluster are the two halves of
    // one `layoutId`, and a group per component would give them two different
    // ids and never pair them.
    <LayoutGroup id={`nook:${nook.id}`}>
      <div className="nook">
        <header className="nook-header">
          <div className="nook-header-title-group">
            <h1 className="nook-header-title">{nook.name}</h1>
            {nook.topic && <p className="nook-topic">{nook.topic}</p>}
          </div>

          {/* The one primary action in the header — and only until you take it.
           * Once you are in, this surface is down in the stage, beside the
           * people it controls. */}
          <VoiceJoinButton nookId={nook.id} />
        </header>

        {/* Above the timeline and never instead of it: text is the floor, voice
         * is ambient. Full view is the one exception, and it is a thing you
         * asked for. */}
        <VoiceStage
          nookId={nook.id}
          participants={nook.voiceParticipants}
          myId={me.id}
          canModerate={canModerate}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((open) => !open)}
        />

        {!expanded && (
          <>
            <MessageTimeline
              nookId={nookId}
              nookName={nook.name}
              myId={me.id}
              myHandle={me.handle}
              canModerate={canModerate}
              onReply={setReplyingTo}
            />

            <TypingIndicator nookId={nookId} myId={me.id} />

            <Composer
              nookId={nookId}
              nookName={nook.name}
              me={me}
              replyingTo={replyingTo}
              onCancelReply={() => setReplyingTo(null)}
            />
          </>
        )}
      </div>
    </LayoutGroup>
  )
}
